import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { queryKeys } from './queryKeys';
import type { Tables } from '@/integrations/supabase/types';
import { toISODate } from "@/lib/dates";

// The database column is text with CHECK (type IN ('income', 'expense')); the generated
// types cannot express the CHECK, so narrow it here.
export type Transaction = Omit<Tables<'transactions'>, 'type'> & { type: 'income' | 'expense' };

interface UseTransactionsOptions {
  dateFrom?: string;
  dateTo?: string;
  type?: 'income' | 'expense';
  enabled?: boolean;
}

/**
 * Fetch all transactions with optional filters
 * Used by: Dashboard, Insights, Accounts, Reports
 */
export function useTransactions(options: UseTransactionsOptions = {}) {
  const { dateFrom, dateTo, type, enabled = true } = options;

  return useQuery({
    queryKey: queryKeys.transactions.list({ dateFrom, dateTo, type }),
    queryFn: async () => {
      let query = supabase
        .from('transactions')
        .select('*')
        .order('date', { ascending: false });

      if (dateFrom) query = query.gte('date', dateFrom);
      if (dateTo) query = query.lte('date', dateTo);
      if (type) query = query.eq('type', type);

      const { data, error } = await query;
      if (error) throw error;
      return (data || []) as Transaction[];
    },
    enabled,
  });
}

/**
 * Fetch transactions for a specific time period
 * Used by: Trends page (6 months), Reports
 */
export function useRecentTransactions(months: number = 6) {
  const dateFrom = new Date();
  dateFrom.setMonth(dateFrom.getMonth() - months);
  const dateFromStr = toISODate(dateFrom);

  return useQuery({
    queryKey: queryKeys.transactions.recent(months),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('transactions')
        .select('*')
        .gte('date', dateFromStr)
        .order('date', { ascending: true });

      if (error) throw error;
      return (data || []) as Transaction[];
    },
  });
}

/**
 * Get spending by category for a date range
 * Used by: Budgets page for budget vs actual comparison
 */
export function useSpendingByCategory(startDate: string, endDate: string) {
  return useQuery({
    queryKey: queryKeys.budgets.spending({ start: startDate, end: endDate }),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('transactions')
        .select('category, amount, date')
        .eq('type', 'expense')
        .gte('date', startDate)
        .lte('date', endDate);

      if (error) throw error;

      // Aggregate by category
      const spendingByCategory: Record<string, number> = {};
      data?.forEach((t) => {
        const category = t.category || 'Uncategorized';
        spendingByCategory[category] = (spendingByCategory[category] || 0) + Number(t.amount);
      });

      return spendingByCategory;
    },
    enabled: !!startDate && !!endDate,
  });
}
